import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import { CheckCircleOutline, MoreVert, AttachFile } from "@mui/icons-material";

import { CardLayout, DeleteModal, EditDeleteMenu } from "../v3";
import { AddEditTaskProgress } from "./AddEditTaskProgress.component";
import { TaskService } from "../../services";
import { getDateAndTime } from "../../utils/formatters";
import { DELETE_TASK_PROGRESS, EDIT_TASK_PROGRESS } from "../../constants";
import { useHasPermission } from "../../utils";
import { TaskProgressAttachmentsModal } from "./TaskProgressAttachmentsModal.component";

export const TaskProgressActivity = ({
  totalCount = 0,
  setTotalCount = () => {},
  taskDetails,
  taskProgressQuantity,
  setTaskProgressQuantity = () => {},
  reloadData,
  fetchTaskDetails = () => {},
}) => {
  const [taskProgress, setTaskProgress] = useState([]);
  const [taskProgressId, setTaskProgressId] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    taskProgressId: null,
    quantity: undefined,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [attachmentsProgress, setAttachmentsProgress] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentCounts, setAttachmentCounts] = useState({});

  const openMenu = Boolean(anchorEl);

  const { id } = useParams();

  const hasTaskProgressEdit = useHasPermission(EDIT_TASK_PROGRESS);
  const hasTaskProgressDelete = useHasPermission(DELETE_TASK_PROGRESS);

  useEffect(() => {
    fetchData();
  }, [id, reloadData]);

  const fetchData = async (taskProgressFilters = {}) => {
    try {
      const { data: progress } = await TaskService.getProgressList(id, {
        ...taskProgressFilters,
        includeAttachments: true,
      });

      const rows = progress?.rows ?? [];

      const totalQuantity = rows.reduce((sum, item) => sum + Number(item?.quantity ?? 0), 0);

      setTaskProgressQuantity(totalQuantity);
      setTaskProgress(rows);
      setTotalCount(progress?.count);

      const counts = {};

      rows.forEach((item) => {
        counts[item.id] = item.attachmentsCount ?? 0;
      });

      setAttachmentCounts(counts);
    } catch (error) {
      toast.error("Failed to fetch tasks");
    }
  };

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedProgress(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProgress(null);
  };

  const handleEdit = () => {
    setTaskProgressId(selectedProgress?.id);
    setOpen(true);
    handleMenuClose();
  };

  const openDeleteModal = () => {
    setDeleteModal({ isOpen: true, taskProgressId: selectedProgress?.id, quantity: selectedProgress?.quantity });
    handleMenuClose();
  };

  const fetchAttachments = async (progressId) => {
    setAttachmentsLoading(true);

    try {
      const { data } = await TaskService.getTaskProgressAttachments(id, progressId);

      setAttachments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch attachments:", error);
      toast.error("Failed to fetch task progress attachments");
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleOpenAttachments = (progress) => {
    setAttachmentsProgress(progress);
    setAttachments([]);
    fetchAttachments(progress.id);
  };

  const handleCloseAttachments = () => {
    setAttachmentsProgress(null);
    setAttachments([]);
  };
  const closeDeleteModal = () => setDeleteModal({ isOpen: false, taskProgressId: null, quantity: null });

  const handleDelete = async () => {
    try {
      // deleting progresses when task is completed
      if (taskProgressQuantity === taskDetails?.quantity) {
        try {
          // update status to TODO when there is only single progress
          if (taskProgressQuantity - deleteModal.quantity === 0) await TaskService.updateTask(id, { status: "TODO" });
          // update status to INPROGRESS where there are multiple progresses
          else await TaskService.updateTask(id, { status: "INPROGRESS" });
        } catch (error) {
          toast.error("Failed to delete task progress");
          return;
        }

        try {
          // delete task progress after updating task status
          await TaskService.deleteTaskProgress(id, deleteModal.taskProgressId);
        } catch (error) {
          try {
            // rollback to DONE when deleting progress fails
            await TaskService.updateTask(id, { status: "DONE" });
          } catch (error) {
            console.error("Rollback failed:", error);
          }
          toast.error("Failed to delete task progress");
          return;
        }
        // deleting progress when task is not completed
      } else if (taskProgressQuantity !== taskDetails?.quantity) {
        // update status to TODO, while deleting the final progress
        if (taskProgressQuantity - deleteModal.quantity === 0) {
          try {
            await TaskService.updateTask(id, { status: "TODO" });
          } catch (error) {
            toast.error("Failed to delete task progress");
            return;
          }
        }

        try {
          // delete task
          await TaskService.deleteTaskProgress(id, deleteModal.taskProgressId);
        } catch (error) {
          try {
            // rollback status to INPROGRESS when deleting progress fails
            await TaskService.updateTask(id, { status: "INPROGRESS" });
          } catch (error) {
            console.error("Rollback failed:", error);
          }
          toast.error("Failed to delete task progress");
          return;
        }
      }
      toast.success("Task progress deleted successfully");
      fetchData();
      fetchTaskDetails();
    } catch (error) {
      toast.error("Failed to delete task progress");
      console.error("🚀 ~ handleDelete ~ error:", error);
    } finally {
      closeDeleteModal();
    }
  };

  if (totalCount === 0) return null;

  return (
    <>
      <CardLayout>
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              left: "9px",
              top: 0,
              height: "calc(100% - 20px)",
              width: "2px",
              backgroundColor: "#e0e0e0",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: "9px",
              bottom: 0,
              height: "20px",
              width: "0px",
              borderLeft: "2px dotted #e0e0e0",
            }}
          />

          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f0f0f0",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c0c0c0",
                borderRadius: "4px",
                border: "2px solid #f0f0f0",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#a0a0a0",
              },
            }}
          >
            {taskProgress.map((item) => (
              <Box key={item.id} display="flex" alignItems="flex-start" mb={3} position="relative">
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    border: "2px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <CheckCircleOutline fontSize="small" color="action" />
                </Box>

                <Box pl={6} flex={1}>
                  <Typography variant="body2" fontWeight={500} mb={1}>
                    {item?.description}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Chip
                      label={`${item?.quantity} ${taskDetails?.unitOfMeasure}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#674BF5",
                        color: "#674BF5",
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {getDateAndTime(item?.updatedAt)}
                  </Typography>

                  {attachmentCounts[item.id] > 0 && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      mt={1}
                      sx={{
                        width: "fit-content",
                        cursor: "pointer",
                        color: "#674BF5",
                      }}
                      onClick={() => handleOpenAttachments(item)}
                    >
                      <AttachFile fontSize="small" />

                      <Typography variant="body2" fontWeight={500}>
                        View Attachments ({attachmentCounts[item.id]})
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, item)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          {/* edit delete menu */}
          <EditDeleteMenu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            onEdit={handleEdit}
            onDelete={openDeleteModal}
            canEdit={hasTaskProgressEdit}
            canDelete={hasTaskProgressDelete}
          />
        </Box>
      </CardLayout>

      {/* edit task progress */}
      <AddEditTaskProgress
        open={open}
        onClose={() => setOpen(false)}
        progressId={taskProgressId}
        taskQuantity={taskDetails?.quantity}
        taskProgressQuantity={taskProgressQuantity}
        taskStatus={taskDetails?.status}
        onSuccess={() => {
          fetchData();
          fetchTaskDetails();
        }}
      />
      {/* Task progress attachments */}
      <TaskProgressAttachmentsModal
        open={Boolean(attachmentsProgress)}
        onClose={handleCloseAttachments}
        loading={attachmentsLoading}
        documents={attachments.map((attachment) => ({
          id: attachment.id,
          originalName: attachment.uploadedFile?.originalName ?? "Unnamed document",
          createdAt: attachment.createdAt,
          url: attachment.uploadedFile?.url,
        }))}
        onView={(document) => {
          if (!document.url) {
            toast.error("Document URL is unavailable");
            return;
          }

          window.open(document.url, "_blank", "noopener,noreferrer");
        }}
        onDelete={async (attachmentId) => {
          if (!attachmentsProgress?.id) {
            throw new Error("Task progress ID is missing");
          }

          try {
            const response = await TaskService.deleteTaskProgressAttachment(id, attachmentsProgress.id, attachmentId);

            if (response.data?.success === false) {
              throw new Error("Backend did not delete the attachment");
            }

            setAttachments((prev) => prev.filter((attachment) => String(attachment.id) !== String(attachmentId)));
            setAttachmentCounts((prev) => ({
              ...prev,
              [attachmentsProgress.id]: Math.max(0, (prev[attachmentsProgress.id] ?? 0) - 1),
            }));
            toast.success("Attachment deleted successfully");
          } catch (error) {
            console.error("Failed to delete attachment:", error);
            toast.error("Failed to delete attachment");
            throw error;
          }
        }}
      />
      {/* delete task progress */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this task progress?"
      />
    </>
  );
};
